import { axiosInstance, ValidationError } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getToday } from 'utils/shiftedDateTime';
import { ProductNumberKind } from 'hooks/webapi/useClient';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

type TransactionType =
  | 'MAIN'
  | 'EXTENSION'
  | 'NEW_AMOUNT'
  | 'DEBT_COLLECTION'
  | 'PRINCIPAL_COLLECTION'
  | 'RESCHEDULED'
  | 'RENOUNCEMENT'
  | 'INTEREST_RATE_CHANGE'
  | 'MAIN_RESCH'
  | 'EXTENSION_RESCH'
  | 'NEW_AMOUNT_RESCH'
  | 'CANCELLED_DISCOUNTS'
  | 'RECALCULATED'
  | 'RECALCULATED_DELAYED'
  | 'CONTRACTUAL_RESCH';

export type LoanExtension = {
  expirationDate?: string;
  discountPercentage?: number;
  fee: number;
  feeBeforeDiscount: number;
  feeWithoutInterest: number;
  interest: number;
  term: number;
  unit: 'DAYS' | 'MONTHS';
};
type Term = {
  termUnit: 'DAYS' | 'MONTHS';
  value: number;
};

export type LatestLoan = {
  id: number;
  productCode: 'MINI' | 'CONS'; // MINI is single payments loans & CONS is instalment loans
  productInfo: ProductNumberKind;
  payNowAmount: number; // Amount required to be paid for MINI product
  payableAmount: number; // Amount required to be paid for current single instalment on CONS product
  loanExtensionFees: Array<LoanExtension>;
  autoRepay: boolean;
  delayedAmountDue: number;
  dueDate: string;
  isExtensionPossible: boolean;
  loanNumber: string;
  schedulePayment: number;
  totalOutstandingAmount: number;
  totalPrincipal: number;
  interestAmount: number;
  loanTerm: Term;
  mainAgreementTerm: Term;
  totalInvoicedAmount: number;
  principalAmount: number;
  nearestPaymentDate: string;
  penaltyAmount: number;
  penaltyRatePerDay?: number;
  overDueDays: number;
  productNumber: string;
  openAmount: number;
  earlyFullRepaymentFee: number;
  transactionType: TransactionType;
  debtCollectLoanAgreement: boolean; // True when loan agreement has been broken
  agreementType: 'MAIN' | 'NEW_AMOUNT' | 'EXTENSION' | 'DEBT_COLLECTION';
  commissionAmount: number;
  gracePeriodInDays: number;
  amount: number;
  totalOutpaidAmount: number;
  // keys which we are adding on the FE, don't mix with values from the BE
  daysLeft: number;
  pendingInterest: number;
  nonDelayedAmountDue: number;
  bundleAccepted?: boolean;
  bundleExtensionPossible?: boolean;
  bundleOffered?: boolean;
  lastDateForNewAmountWhenBundleNotAccepted?: string;
  openAgreementExtraFeesAmount?: number;
  startDate?: string;
};

type AutoRepayBodyType = {
  body: {
    autoRepay: boolean;
  };
};

type PaymentsSummary = {
  repaymentsDistributionsAmount: number;
};

type LatestLoanContextType = {
  latestLoan?: LatestLoan | null;
  selectedLoanExtension?: LoanExtension;
  fetchLatestLoan: () => Promise<LatestLoan | void | null>;
  setSelectedLoanExtension: React.Dispatch<
    React.SetStateAction<LoanExtension | undefined>
  >;
  autoRepay: ({ body }: AutoRepayBodyType) => Promise<LatestLoan | void | null>;
  paymentsSummary: PaymentsSummary | undefined;
  fetchPaymentsSummary: () => Promise<PaymentsSummary | void | null>;
};

const LatestLoanContext = createContext<LatestLoanContextType>(
  {} as LatestLoanContextType,
);

const useLatestLoan = (): LatestLoanContextType =>
  useContext(LatestLoanContext);

const LatestLoanProvider = ({ children }: Props): JSX.Element => {
  const [latestLoan, setLatestLoan] = useState<LatestLoan | null>();
  const [selectedLoanExtension, setSelectedLoanExtension] =
    useState<LoanExtension>();
  const [paymentsSummary, setPaymentsSummary] = useState<PaymentsSummary>();
  const location = useLocation();

  const { showError } = useError();

  const fetchLatestLoan =
    useCallback(async (): Promise<LatestLoan | void | null> => {
      try {
        const response = await axiosInstance.get<LatestLoan>(
          '/client/loans/latest',
        );

        const fetchedLatestLoan = response.data;

        // checking `daysLeft` as we hardcode it in the fixtures for integration tests
        if (fetchedLatestLoan && !fetchedLatestLoan.daysLeft) {
          const today = getToday();

          fetchedLatestLoan.daysLeft = dayjs(fetchedLatestLoan.dueDate).diff(
            dayjs(today),
            'day',
          );
        }

        if (fetchedLatestLoan && fetchedLatestLoan.loanExtensionFees) {
          // Sort extensions from smallest to biggest
          fetchedLatestLoan.loanExtensionFees =
            fetchedLatestLoan.loanExtensionFees.sort((a, b) => {
              const firstElementTerm =
                a.unit === 'MONTHS' ? a.term * 31 : a.term;
              const secondElementTerm =
                b.unit === 'MONTHS' ? b.term * 31 : b.term;

              return firstElementTerm - secondElementTerm;
            });
        }

        setLatestLoan(fetchedLatestLoan);
        return fetchedLatestLoan;
      } catch (e) {
        // 404 is thrown if latestLoan doesn't exist. In this case we don't want to throw errors
        if (e instanceof Error && e.message.includes('404')) {
          setLatestLoan(null);
          return null;
        }
        if (!(e as ValidationError)?.message.includes('401')) {
          showError();
          throw e;
        }
      }
    }, [showError]);

  useEffect(() => {
    const prefetch = async () => {
      if (latestLoan === undefined && isLogged()) {
        try {
          await fetchLatestLoan();
        } catch {}
      }
    };
    prefetch();
  }, [location]);

  const autoRepay = useCallback(
    async ({ body }: AutoRepayBodyType): Promise<LatestLoan | void | null> => {
      try {
        const response = await axiosInstance.post(
          `/client/loans/latest/auto-repay`,
          body,
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          },
        );
        const data = response.data;
        setLatestLoan(data);
      } catch (e) {
        if (!axios.isAxiosError(e)) {
          showError();
          throw e;
        }
        // Don't throw error otherwise
      }
    },
    [showError],
  );

  const fetchPaymentsSummary =
    useCallback(async (): Promise<PaymentsSummary | void> => {
      try {
        const response = await axiosInstance.get<PaymentsSummary>(
          `/client/loans/latest/payments/summary`,
        );
        setPaymentsSummary(response.data);
      } catch (e) {
        if ((e as ValidationError)?.message !== '404')
          showError(e as ValidationError);
        throw e;
      }
    }, [showError]);

  const latestLoanContextValue = useMemo(
    () => ({
      latestLoan,
      selectedLoanExtension,
      fetchLatestLoan,
      setSelectedLoanExtension,
      autoRepay,
      fetchPaymentsSummary,
      paymentsSummary,
    }),
    [
      latestLoan,
      selectedLoanExtension,
      fetchLatestLoan,
      autoRepay,
      fetchPaymentsSummary,
      paymentsSummary,
    ],
  );

  return (
    <LatestLoanContext.Provider value={latestLoanContextValue}>
      {children}
    </LatestLoanContext.Provider>
  );
};

export { useLatestLoan as default, LatestLoanProvider };
